<script>
import { storeToRefs } from 'pinia'
import { useUserStore } from "./stores/user";
import { useRegionStore } from "./stores/region";
import { useSessionStore } from "./stores/session";
import { useSocketStore } from "./stores/socket";
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { provide } from 'vue'
import { useQuasar } from 'quasar'
import PasswordCompromiseBanner from "./components/PasswordCompromiseBanner.vue";
import SecurityBanner from "./components/SecurityBanner.vue";
import { useNotificationSound, hasIncomingFromOthers } from "./composables/useNotificationSound";
import { deviceKindLabel } from "./composables/deviceKind";
import { programs } from "./data/marketingPrograms";
export default {
  components: {
    PasswordCompromiseBanner,
    SecurityBanner,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const leftDrawerOpen = ref(false)
    const chatDrawerOpen = ref(false)
    const userStore = useUserStore();
    const regionStore = useRegionStore();
    const socketStore = useSocketStore();
    const { user, lti_launch, showPasswordCompromiseBanner } = storeToRefs(userStore);
    const { org } = storeToRefs(regionStore);
    // The organization's security notice (GitLab #1072), once on entering the
    // app after login unless the login page already showed it.
    const sessionStore = useSessionStore();
    const { securityBannerText } = storeToRefs(sessionStore);
    function dismissSecurityBanner() {
      sessionStore.markSecurityBannerSeen(org.value && org.value.uid);
    }
    const { simulationContext, messages, loadingHistory } = storeToRefs(socketStore);
    const unreadCount = ref(0);

    // The registered-device signal, shared by both user dropdowns so the two
    // can't drift. `userStore.device` is the `device` object `/user_session`
    // returns alongside the session -- the only way to know it: the
    // `device_id` cookie is HttpOnly and, in production, scoped to the API's
    // `prod.chartflow.io`, which the `www`/org-subdomain site cannot read.
    const current_device_id = computed(() => (userStore.device && userStore.device.uid) || null);
    const show_device_entry = computed(() => {
      return userStore.default_route !== '/student' && !!current_device_id.value;
    });
    // What this computer is -- "Cabinet computer · <cabinet>" or
    // "Kiosk · <activity>" -- from the device record alone (its cabinet name
    // is embedded at registration), so the menu costs no extra request.
    const device_kind = computed(() => deviceKindLabel(userStore.device));

    const isSimulationActive = computed(() => {
      return !!(simulationContext.value && simulationContext.value.simulation_uid && socketStore.ws && socketStore.ws.status === 'OPEN');
    });
    // Check if user is in an LTI session
    const isLTISession = computed(() => {
      return lti_launch.value && Object.keys(lti_launch.value).length > 0;
    });

    const $q = useQuasar();

    // Audio cue for incoming SimChat messages/notifications. The sound is an
    // additional signal on top of the visual unread badge (never the only cue),
    // is muteable, and throttled to avoid ding-spam. See useNotificationSound.
    const { playDing } = useNotificationSound();

    // Determine when to show hamburger menu
    const shouldShowHamburgerMenu = computed(() => {
      if (!user.value) return false;

      // Don't show hamburger for any student routes (StudentLayout has no drawer)
      if (route.path.startsWith('/student')) {
        return false;
      }

      // Show for all other routes
      return true;
    });

    provide('leftDrawerOpen', leftDrawerOpen);
    provide('chatDrawerOpen', chatDrawerOpen);

    // Track unread messages when chat drawer is closed
    // Only increment when messages are added (length increases), not on clear
    let lastMessageCount = 0;
    watch(messages, (newMessages) => {
      const newCount = newMessages.length;
      if (newCount > lastMessageCount && !chatDrawerOpen.value && isSimulationActive.value && !loadingHistory.value) {
        unreadCount.value += (newCount - lastMessageCount);
        // Play the audio cue only when at least one of the newly-arrived
        // messages came from someone other than the current user.
        const added = newMessages.slice(lastMessageCount);
        if (hasIncomingFromOthers(added, user.value && user.value.uid)) {
          playDing();
        }
      }
      lastMessageCount = newCount;
    }, { deep: true });

    watch(chatDrawerOpen, (val) => {
      if (val) {
        unreadCount.value = 0;
        lastMessageCount = messages.value.length;
      }
    });

    // Close chat drawer when simulation disconnects
    watch(isSimulationActive, (val) => {
      if (!val) {
        chatDrawerOpen.value = false;
      }
    });

    // A session established in-app (login, OTP, passkey) goes through
    // set_session, which carries no device. For staff -- the only users the
    // device entry is shown to -- resolve it once; after a page load the boot
    // /user_session has already answered and this issues nothing.
    watch(() => user.value && userStore.default_route !== '/student' && !userStore.deviceLoaded, (needed) => {
      if (needed) {
        // A failure is reported by the un-intercepted /user_session client;
        // the menu simply shows no device entry.
        userStore.ensureDevice().catch(() => {});
      }
    }, { immediate: true });

    const hasMultipleSchools = computed(() => {
      return user.value && user.value.schools && user.value.schools.length > 1
    })

    async function switchSchool(school) {
      $q.loading.show()
      try {
        await userStore.UpdateSession(school.organization_uid)
        // Force a full session refresh (bypass the cache) so the new org and
        // the user's roles for it are loaded before we compute the landing route.
        await userStore.getUserSession(true)
        // Hard reload to ensure all components re-mount with new org context
        const targetRoute = userStore.default_route
        window.location.href = targetRoute
      } catch (e) {
        $q.loading.hide()
      }
    }

    return {
      route,
      programs,
      current_device_id,
      show_device_entry,
      device_kind,
      userStore,
      user,
      org,
      isLTISession,
      showPasswordCompromiseBanner,
      securityBannerText,
      dismissSecurityBanner,
      leftDrawerOpen,
      chatDrawerOpen,
      isSimulationActive,
      simulationContext,
      unreadCount,
      hasMultipleSchools,
      switchSchool,
      shouldShowHamburgerMenu,
      toggleLeftDrawer() {
        leftDrawerOpen.value = !leftDrawerOpen.value
      },
      toggleChatDrawer() {
        chatDrawerOpen.value = !chatDrawerOpen.value
      },
      forget_device() {
        $q.loading.show();
        regionStore.DeleteDevice(current_device_id.value).then(() => {
          // DELETE /device/{uid} expired the cookie; the menu reads the store.
          userStore.setDevice(null)
        }).finally(() => {
          $q.loading.hide()
        })
      },
      skipToMain() {
        const main = document.getElementById('main-content')
        if (main) {
          main.focus()
          main.scrollIntoView()
        }
      }

    }
  },
}
</script>

<template>
  <q-layout view="hHh lpR fFR">

    <a href="#main-content" class="skip-link" @click.prevent="skipToMain">Skip to main content</a>

    <q-header elevated class="bg-white text-black print-hide">
      <q-toolbar class="q-px-xs-sm">
        <q-btn v-if="user && shouldShowHamburgerMenu" class="lt-md" dense flat round icon="menu"
          @click="toggleLeftDrawer" aria-label="Toggle navigation menu" />

        <q-toolbar-title v-if="$q.screen.lt.md && route.fullPath.startsWith('/patient')" class="q-px-xs col-shrink">
          <router-link class="logo-link q-mt-md" :to="userStore.default_route" aria-label="ChartFlow home">
            <img alt="Chartflow Logo" height="50" src="/icon-blue.svg">
          </router-link>
        </q-toolbar-title>
        <q-toolbar-title v-else class="col-shrink">
          <router-link v-if="route.path !== userStore.default_route" class="logo-link q-mt-md q-pl-sm" :to="userStore.default_route" aria-label="ChartFlow home">
            <img alt="Chartflow Logo" height="30" src="/logo.svg">
          </router-link>
          <span v-else class="logo-link q-mt-md q-pl-sm" aria-hidden="true">
            <img alt="" height="30" src="/logo.svg">
          </span>
          <!-- Org display: dropdown when multiple schools, static otherwise -->
          <template v-if="org && user">
            <!-- Multiple schools: dropdown switcher -->
            <div v-if="hasMultipleSchools" class="block row" style="margin-top: -15px;">
              <span class="q-pl-sm q-mb-sm text-primary text-caption cursor-pointer">
                <q-avatar v-if="org.logo" size="xs">
                  <img :src="org.logo" alt="">
                </q-avatar>
                {{ org.name }}
                <q-icon name="expand_more" size="xs" />
                <q-menu>
                  <q-list>
                    <q-item
                      v-for="school in user.schools"
                      :key="school.organization_uid"
                      clickable
                      v-close-popup
                      @click="switchSchool(school)"
                      :active="school.organization_uid === org.uid"
                    >
                      <q-item-section>
                        <q-item-label>{{ school.organization_name }}</q-item-label>
                      </q-item-section>
                      <q-item-section side v-if="school.organization_uid === org.uid">
                        <q-icon name="check" color="primary" />
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </span>
              <router-link
                v-if="userStore.check_roles(['Superuser', 'Support'])"
                :to="`/organization/${org.uid}`"
                class="text-primary q-mb-sm"
                aria-label="Open organization settings"
              >
                <q-icon name="settings" size="xs" />
                <q-tooltip>Organization settings</q-tooltip>
              </router-link>
            </div>
            <!-- Single school: admin link or plain text -->
            <router-link v-else-if="userStore.check_roles(['Superuser', 'Support', 'Organization Admin'], org.uid)" class="block row" style="margin-top: -15px;"
              :to="`/organization/${org.uid}`">
              <span class="q-pl-sm q-mb-sm text-primary text-caption">
                <q-avatar v-if="org.logo" size="xs">
                  <img :src="org.logo" alt="">
                </q-avatar>
                {{ org.name }}
                {{ userStore.check_roles(['Superuser', 'Support'], org.uid) ? `(${org.region})` : '' }}
              </span>
            </router-link>
            <div v-else class="block row" style="margin-top: -15px;">
              <span class="q-pl-sm q-mb-sm text-primary text-caption">
                <q-avatar v-if="org.logo" size="xs">
                  <img :src="org.logo" alt="">
                </q-avatar>
                {{ org.name }}
              </span>
            </div>
          </template>
        </q-toolbar-title>
        <div class="row items-center justify-center" :class="{ 'col': user }" id="toolbar_container">
          <nav v-if="user && org && org.billing_level === 'B2C' && $route.path === '/student' && $q.screen.gt.sm" aria-label="Main navigation">
            <ul class="nav-list row items-center no-wrap q-gutter-x-xs">
              <li><q-btn color="primary" flat to="/catalog" class=text-bold>Course Catalog</q-btn></li>
            </ul>
          </nav>
        </div>
        <nav class="col row justify-center marketing-nav-wide" v-if="!user" aria-label="Main navigation">
          <ul class="nav-list row items-center no-wrap q-gutter-x-xs">
            <li><q-btn color="primary" flat to="/features" class=text-bold>Features</q-btn></li>
            <li>
              <q-btn-dropdown color="primary" stretch flat label="Programs" class=text-bold>
                <q-list>
                  <q-item v-for="program in programs" :key="program.slug" clickable v-close-popup :to="`/programs/${program.slug}`">
                    <q-item-section>
                      <q-item-label>{{ program.name }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </li>
            <li><q-btn color="primary" flat to="/#pricing" class=text-bold>Pricing</q-btn></li>
            <li><q-btn color="primary" flat to="/catalog" class=text-bold>Course Catalog</q-btn></li>
            <li>
              <q-btn-dropdown color="primary" stretch flat label="Resources" class=text-bold>
                <q-list>
                  <q-item clickable v-close-popup href="https://blog.chartflow.io" target="_blank">
                    <q-item-section>
                      <q-item-label>Blog</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item clickable v-close-popup href="https://publishing.chartflow.io/" target="_blank">
                    <q-item-section>
                      <q-item-label>Publishing</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item clickable v-close-popup href="https://help.chartflow.io" target="_blank">
                    <q-item-section>
                      <q-item-label>Help Center</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </li>
            <li><q-btn color="primary" flat to="/contact" class=text-bold>Contact Us</q-btn></li>
          </ul>
        </nav>
        <!-- Menu button — shown whenever the full nav does not fit (small viewports / 200% zoom) -->
        <q-btn-dropdown v-if="!user" class="marketing-nav-narrow" icon="menu" flat color="primary" aria-label="Navigation menu" dropdown-icon="none">
          <q-list>
            <q-item clickable v-close-popup to="/features">
              <q-item-section><q-item-label>Features</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup to="/#pricing">
              <q-item-section><q-item-label>Pricing</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup to="/contact">
              <q-item-section><q-item-label>Contact Us</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup to="/signup">
              <q-item-section><q-item-label>New Student Account</q-item-label></q-item-section>
            </q-item>
            <q-separator />
            <div role="group" aria-labelledby="mobile-nav-programs-label">
              <q-item-label header id="mobile-nav-programs-label">Programs</q-item-label>
              <q-item v-for="program in programs" :key="program.slug" clickable v-close-popup :to="`/programs/${program.slug}`">
                <q-item-section><q-item-label>{{ program.name }}</q-item-label></q-item-section>
              </q-item>
            </div>
            <q-separator />
            <q-item clickable v-close-popup to="/catalog">
              <q-item-section><q-item-label>Course Catalog</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup href="https://blog.chartflow.io" target="_blank">
              <q-item-section><q-item-label>Blog</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup href="https://publishing.chartflow.io/" target="_blank">
              <q-item-section><q-item-label>Publishing</q-item-label></q-item-section>
            </q-item>
            <q-item clickable v-close-popup href="https://help.chartflow.io" target="_blank">
              <q-item-section><q-item-label>Help Center</q-item-label></q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <q-space />
        <div class="col col-shrink">
          <q-btn class="text-bold marketing-nav-wide" v-if="!user" color="primary" flat to="/signup">New Student Account</q-btn>
          <q-btn v-if="!user" color="secondary" to="/login" class="text-bold">Login</q-btn>

          <!-- LTI Session Indicator (Desktop) -->
          <q-chip class="gt-sm q-mr-sm" v-if="isLTISession" color="info" text-color="white" size="sm">
            <q-icon name="sync" class="q-mr-xs" size="xs" />
            LTI Session
          </q-chip>

          <q-avatar class="gt-sm" v-if="user" color="primary" aria-hidden="true">
            <span class="text-body2 text-white" v-if="!user.picture">{{ user.initials }}</span>
            <img v-else :src="user.picture" />
          </q-avatar>
          <q-btn-dropdown class="gt-xs" v-if="user" color="primary" stretch flat>
            <template v-slot:label>
              <div class="column items-start no-wrap user-menu-label">
                <span>{{ user.username }}</span>
                <span v-if="show_device_entry" class="text-caption user-menu-device">{{ device_kind }}</span>
              </div>
            </template>
            <q-list role="menu">
              <q-item clickable v-close-popup to="/profile" role="menuitem">
                <q-item-section>
                  <q-item-label>Profile</q-item-label>
                </q-item-section>
              </q-item>
              <q-item v-if="userStore.default_route !== '/student'" clickable v-close-popup to="/undelete" role="menuitem">
                <q-item-section>
                  <q-item-label>Recycle Bin</q-item-label>
                </q-item-section>
              </q-item>
              <!-- What this browser is registered as. The row opens the devices page. -->
              <q-item v-if="show_device_entry" clickable v-close-popup to="/devices" role="menuitem"
                data-test="device-entry">
                <q-item-section avatar>
                  <q-icon name="devices" aria-hidden="true" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ device_kind }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-separator v-if="show_device_entry" />
              <q-item v-if="show_device_entry" clickable v-close-popup
                @click="forget_device()" role="menuitem">
                <q-item-section>
                  <q-item-label>Forget Device</q-item-label>
                </q-item-section>
              </q-item>
              <q-item v-if="userStore.default_route !== '/student'" clickable v-close-popup to="/devices" role="menuitem">
                <q-item-section>
                  <q-item-label>Devices</q-item-label>
                </q-item-section>
              </q-item>
              <q-item clickable v-close-popup to="/logout" role="menuitem">
                <q-item-section>
                  <q-item-label>Log Out</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <q-btn-dropdown class="gt-xs" v-if="user" stretch flat label="Support" icon="help_outline" color="secondary">
            <q-list role="menu">
              <q-item clickable v-close-popup href="https://help.chartflow.io" target="_blank" role="menuitem">
                <q-item-section>
                  <q-item-label>Help Center</q-item-label>
                </q-item-section>
              </q-item>
              <q-item
                v-if="userStore.check_roles(['Superuser', 'Support', 'Instructor', 'Organization Admin', 'Program Admin', 'Campus Admin', 'Adjunct'], org && org.uid)"
                clickable v-close-popup href="https://resources.chartflow.io" target="_blank" role="menuitem">
                <q-item-section>
                  <q-item-label>Instructor Resources</q-item-label>
                </q-item-section>
              </q-item>
              <q-item clickable v-close-popup href="https://help.chartflow.io/articles/new-order" target="_blank" role="menuitem">
                <q-item-section>
                  <q-item-label>New Order Request</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </div>
        <q-btn v-if="isSimulationActive" flat round :icon="chatDrawerOpen ? 'chat' : 'chat_bubble_outline'" :color="chatDrawerOpen ? 'secondary' : 'primary'" @click="toggleChatDrawer" class="q-ml-sm" aria-label="Toggle SimChat">
          <q-badge v-if="unreadCount > 0" color="negative" floating>{{ unreadCount }}</q-badge>
          <q-tooltip>SimChat</q-tooltip>
        </q-btn>
      </q-toolbar>
      <q-toolbar v-if="user" class="lt-sm">
        <!-- LTI Session Indicator (Mobile) -->
        <q-chip class="lt-md q-mr-sm" v-if="isLTISession" color="info" text-color="white" size="sm">
          <q-icon name="sync" class="q-mr-xs" size="xs" />
          LTI
        </q-chip>

        <q-avatar v-if="user" color="primary" aria-hidden="true">
          <span class="text-body2" v-if="!user.picture">{{ user.initials }}</span>
          <img v-else :src="user.picture" />
        </q-avatar>
        <q-btn-dropdown v-if="user" color="primary" stretch flat>
          <template v-slot:label>
            <div class="column items-start no-wrap user-menu-label">
              <span>{{ user.username }}</span>
              <span v-if="show_device_entry" class="text-caption user-menu-device">{{ device_kind }}</span>
            </div>
          </template>
          <q-list role="menu">
            <q-item clickable v-close-popup to="/profile" role="menuitem">
              <q-item-section>
                <q-item-label>Profile</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="userStore.default_route !== '/student'" clickable v-close-popup to="/undelete" role="menuitem">
              <q-item-section>
                <q-item-label>Recycle Bin</q-item-label>
              </q-item-section>
            </q-item>
            <!-- What this browser is registered as. The row opens the devices page. -->
            <q-item v-if="show_device_entry" clickable v-close-popup to="/devices" role="menuitem"
              data-test="device-entry">
              <q-item-section avatar>
                <q-icon name="devices" aria-hidden="true" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ device_kind }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-separator v-if="show_device_entry" />
            <q-item v-if="show_device_entry" clickable v-close-popup
              @click="forget_device()" role="menuitem">
              <q-item-section>
                <q-item-label>Forget Device</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="userStore.default_route !== '/student'" clickable v-close-popup to="/devices" role="menuitem">
              <q-item-section>
                <q-item-label>Devices</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup to="/logout" role="menuitem">
              <q-item-section>
                <q-item-label>Log Out</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
        <q-btn-dropdown v-if="user" stretch flat label="Support" icon="help_outline" color="secondary">
          <q-list role="menu">
            <q-item clickable v-close-popup href="https://help.chartflow.io" target="_blank" role="menuitem">
              <q-item-section>
                <q-item-label>Help Center</q-item-label>
              </q-item-section>
            </q-item>
            <q-item
              v-if="userStore.check_roles(['Superuser', 'Support', 'Instructor', 'Organization Admin', 'Program Admin', 'Campus Admin', 'Adjunct'], org && org.uid)"
              clickable v-close-popup href="https://resources.chartflow.io" target="_blank" role="menuitem">
              <q-item-section>
                <q-item-label>Instructor Resources</q-item-label>
              </q-item-section>
            </q-item>
            <q-item clickable v-close-popup href="https://help.chartflow.io/articles/new-order" target="_blank" role="menuitem">
              <q-item-section>
                <q-item-label>New Order Request</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </q-toolbar>
      <q-banner v-if="org && org.region === 'ap-southeast-1'" class="bg-negative"><span class="text-white"><span
            class="text-bold">Important:</span> We recently completed a data migration that resulted in the removal of
          Courses, Activities, and Patients associated with Free Demo Accounts.
          If you'd like to continue using ChartFlow, send us an email at: <a class="text-white"
            href="mailto:sales@chartflow.io">sales@chartflow.io</a></span></q-banner>
      <PasswordCompromiseBanner v-if="user" />
      <SecurityBanner
        v-if="securityBannerText"
        class="q-ma-sm"
        :text="securityBannerText"
        dismissible
        @dismiss="dismissSecurityBanner"
      />
    </q-header>
    <router-view></router-view>

  </q-layout>
</template>

<style lang="scss">
@import './css/accessibility.scss';

// User-menu trigger: the username stays the primary text and the registered
// device name sits under it as a caption. Both lines are line-height-clamped
// and the caption is ellipsised, so a two-line label (and a long device name)
// stay inside the toolbar's existing height and width rather than growing the
// whole bar past the logo row.
.user-menu-label {
  line-height: 1.15;
}

.user-menu-device {
  line-height: 1.1;
  opacity: 0.75;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Logged-out header: the full navigation and the menu button are BOTH rendered
// and these rules show exactly one. It is CSS rather than a $q.screen test so
// the prerendered pages, which have no viewport to measure, carry both. The
// hidden one is display: none, so it is out of the tab order and the
// accessibility tree. The full navigation (six items plus New Student Account
// and Login) needs about 1175px on one line; below 1180px the items wrap and
// overlap, so the menu button takes over well before Quasar's md breakpoint
// (1024px). Quasar's breakpoints are fixed, hence a plain media query.
@media (max-width: 1179.98px) {
  .marketing-nav-wide {
    display: none !important;
  }
}

@media (min-width: 1180px) {
  .marketing-nav-narrow {
    display: none !important;
  }
}

// Navigation list: remove default list styling
.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

// Logo link: tighten focus ring around the image, not the full toolbar-title box
.logo-link {
  display: inline-block;
  line-height: 0;
  border-radius: 4px;
  // Prevent toolbar-title flex from stretching the link
  align-self: flex-start;
}

.q-btn {
  text-transform: none;
}

.text-body2 {
  color: #707070
}

.text-body2.text-white,
.text-white .text-body2 {
  color: white;
}

.text-dark .text-body2,
.text-body2.text-dark {
  color: #1d1d1d;
}

.q-table th {
  background-color: $primary;
  color: white;
  font-weight: bold;
}

.q-table thead tr:first-child th {
  top: 0;
}

.q-table thead tr th {
  position: sticky;
  z-index: 1;
}

.q-table tr:nth-child(odd) {
  background-color: $grey-3;
}

.q-table tr:nth-child(even) {
  background-color: #ffffff;
}

.q-table--loading thead tr:last-child th {
  /* height of all previous header rows */
  top: 48px
}

a:link {
  text-decoration: none;
}

@media print {

  .print-hide,
  aside {
    display: none !important;
  }

  .q-page-container {
    padding: 0px !important;
  }
}
</style>
